import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { comics, categories } from "@/mocks/comics";

const siteUrl = "https://teachmanga4.github.io";

export default function MangaDetailPage() {
  const { id = "" } = useParams();
  const [ascending, setAscending] = useState(false);

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);

  const related = useMemo(() => {
    if (!comic) return [];
    return comics.filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat))).slice(0, 6);
  }, [comic]);

  if (!comic) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center pt-16">
          <i className="ri-emotion-sad-line text-4xl text-foreground-500" aria-hidden="true" />
          <p className="mt-3 text-sm text-foreground-700">没有找到这部漫画</p>
          <Link to="/category/all" className="mt-4 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50">
            返回全部漫画
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const chapterList = ascending ? comic.chapters : [...comic.chapters].reverse();
  const categoryNames = comic.categories
    .map((slug) => categories.find((c) => c.slug === slug)?.name)
    .filter(Boolean) as string[];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title={`${comic.title}漫画-在线免费阅读-178漫画网`}
        description={`${comic.description} 在178漫画网在线免费阅读《${comic.title}》下拉式漫画，便捷的浏览方式解放您的双手，最新章节持续更新。`}
        keywords={`${comic.title},在线免费阅读,下拉式漫画,178漫画网`}
        canonical={`${siteUrl}/manga/${comic.id}`}
        image={comic.cover}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Book",
            name: comic.title,
            alternateName: comic.altTitle,
            author: { "@type": "Person", name: comic.author },
            url: `${siteUrl}/manga/${comic.id}`,
            image: comic.cover,
            description: comic.description,
            inLanguage: "zh-CN",
            genre: categoryNames,
            numberOfPages: comic.chapters.length,
            dateModified: comic.updatedAt,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: comic.rating,
              bestRating: "10",
              ratingCount: Math.floor(Number(comic.views.replace(/[万]/g, "0000")) / 100),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
              { "@type": "ListItem", position: 2, name: categoryNames[0] || "全部漫画", item: `${siteUrl}/category/${comic.categories[0]}` },
              { "@type": "ListItem", position: 3, name: comic.title, item: `${siteUrl}/manga/${comic.id}` },
            ],
          },
        ]}
      />

      <Navbar />

      <main id="main-content" className="flex-1 pt-16 md:pt-[72px]">
        {/* Top info */}
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-6 md:px-6 lg:px-10 lg:py-8">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: categoryNames[0] || "全部漫画", to: `/category/${comic.categories[0] || "all"}` },
                { label: comic.title },
              ]}
            />

            <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
              {/* Cover */}
              <div className="mx-auto w-56 sm:w-64 lg:w-full">
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src={comic.cover}
                    alt={`${comic.title}漫画封面`}
                    title={`${comic.title}漫画在线免费阅读-178漫画网`}
                    className="w-full rounded-lg object-cover object-top"
                  />
                  <span className={`absolute left-3 top-3 rounded-md px-2.5 py-1 text-xs font-bold text-background-50 ${comic.status === "连载中" ? "bg-primary-500" : "bg-secondary-500"}`}>
                    {comic.status}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="flex flex-col">
                <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">{comic.title}</h1>
                <p className="mt-1.5 text-sm text-foreground-700">{comic.altTitle}</p>

                <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
                  <div>
                    <dt className="text-xs text-foreground-600">作者</dt>
                    <dd className="mt-1 font-medium text-foreground-950">{comic.author}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-600">分类</dt>
                    <dd className="mt-1 font-medium text-primary-600">{categoryNames.join(" / ")}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-600">人气</dt>
                    <dd className="mt-1 font-medium text-foreground-950">{comic.views}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-600">更新时间</dt>
                    <dd className="mt-1 font-medium text-foreground-950">{comic.updatedAt}</dd>
                  </div>
                </dl>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-accent-600">
                    <i className="ri-star-fill" aria-hidden="true" />
                    <strong className="font-heading text-lg">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1.5 text-foreground-700">
                    <i className="ri-eye-line" aria-hidden="true" /> {comic.views} 人气
                  </span>
                  <span className="flex items-center gap-1.5 text-foreground-700">
                    <i className="ri-heart-3-fill text-primary-500" aria-hidden="true" /> {comic.fans} 收藏
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {comic.tags.map((t) => (
                    <Link
                      key={t}
                      to="/category/all"
                      className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      #{t}
                    </Link>
                  ))}
                </div>

                <p className="mt-5 text-sm leading-7 text-foreground-800">{comic.description}</p>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to={`/read/${comic.id}/${comic.chapters[0].id}`}
                    className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-fill" aria-hidden="true" />
                    开始阅读
                  </Link>
                  <Link
                    to={`/read/${comic.id}/${comic.chapters[comic.chapters.length - 1].id}`}
                    className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-7 text-sm font-bold text-foreground-950 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                    最新章节
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chapter list */}
        <section className="mx-auto w-full px-4 py-10 md:px-6 lg:px-10">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              章节列表
              <span className="ml-2 text-sm font-normal text-foreground-600">共 {comic.chapters.length} 话</span>
            </h2>
            <button
              type="button"
              onClick={() => setAscending((v) => !v)}
              className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-4 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-200"
            >
              <i className={ascending ? "ri-arrow-down-line" : "ri-arrow-up-line"} aria-hidden="true" />
              {ascending ? "正序排列" : "倒序排列"}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {chapterList.map((ch) => (
              <Link
                key={ch.id}
                to={`/read/${comic.id}/${ch.id}`}
                className="group flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-background-50"
              >
                <span className="truncate text-sm text-foreground-800 transition-colors group-hover:text-primary-600">
                  {ch.title}
                </span>
                <span className="shrink-0 text-xs text-foreground-600">{ch.date}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="border-t border-background-200 bg-background-100 py-10 md:py-12">
            <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
              <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">相关漫画推荐</h2>
                <Link
                  to={`/category/${comic.categories[0]}`}
                  className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  更多推荐 <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-6">
                {related.map((item) => (
                  <ComicCard key={item.id} comic={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}